<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import MetricCard from '../overview/MetricCard.vue';
import HeatmapDateRangeSelector from './HeatmapDateRangeSelector.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import liveReportsAPI from 'dashboard/api/liveReports';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import getUnixTime from 'date-fns/getUnixTime';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import { formatTime } from '../../constants';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const teams = useMapGetter('teams/getTeams');

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const fromDate = ref(null);
const toDate = ref(null);
const selectedTeam = ref(null);
const [showDropdown, toggleDropdown] = useToggle();

const isLoading = ref(false);
const timeseriesData = ref([]);
const totalWonCount = ref(0);
const avgClosingTime = ref(0);

const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});

const chartCollection = computed(() => {
  if (!timeseriesData.value.length) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const labels = timeseriesData.value.map(item =>
    format(fromUnixTime(item.timestamp), 'dd MMM')
  );
  const data = timeseriesData.value.map(item => item.value);

  return {
    labels,
    datasets: [
      {
        label: t('OVERVIEW_REPORTS.WON_SALES.WON_METRIC_LABEL'),
        backgroundColor: '#10b981', // emerald-500
        hoverBackgroundColor: '#059669',
        borderRadius: 4,
        barThickness: 16,
        maxBarThickness: 24,
        data,
      },
    ],
  };
});

const hasData = computed(() => {
  return timeseriesData.value.some(item => item.value > 0);
});

const fetchWonSales = async () => {
  isLoading.value = true;
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  if (fromDate.value && toDate.value) {
    params.since = getUnixTime(fromDate.value);
    params.until = getUnixTime(toDate.value);
  }

  try {
    const response = await liveReportsAPI.getWonSalesTimeseries(params);
    timeseriesData.value = response.data?.data || [];
    totalWonCount.value = response.data?.total_count || 0;
    avgClosingTime.value = response.data?.avg_closing_time || 0;
  } catch (error) {
    // Keep the report empty on failure, but expose the real API error for diagnosis.
    console.error(
      'Failed to fetch won sales report',
      `status=${error.response?.status || 'unknown'}`,
      `message=${error.message || 'unknown'}`,
      `response=${JSON.stringify(error.response?.data || null)}`,
      `params=${JSON.stringify(params)}`
    );
    timeseriesData.value = [];
    totalWonCount.value = 0;
    avgClosingTime.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const handleTeamAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchWonSales();
};

const { startRefetching } = useLiveRefresh(fetchWonSales);

watch([fromDate, toDate], () => {
  if (fromDate.value && toDate.value) {
    fetchWonSales();
  }
});

onMounted(() => {
  fetchWonSales();
  startRefetching();
});
</script>

<template>
  <MetricCard
    :header="t('OVERVIEW_REPORTS.WON_SALES.HEADER')"
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.WON_SALES.LOADING_MESSAGE')"
  >
    <template #control>
      <div class="flex items-center gap-2 flex-wrap justify-end">
        <HeatmapDateRangeSelector
          v-model:from="fromDate"
          v-model:to="toDate"
        />
        <div
          v-if="teams.length"
          v-on-clickaway="() => toggleDropdown(false)"
          class="relative flex items-center group z-50"
        >
          <Button
            sm
            slate
            faded
            :label="selectedTeamLabel"
            class="capitalize rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showDropdown"
            :menu-items="teamMenuList"
            class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
            label-class="capitalize"
            @action="handleTeamAction($event)"
          />
        </div>
      </div>
    </template>

    <div class="flex flex-col w-full gap-4 pt-2">
      <!-- Resumen KPI Superior -->
      <div class="flex items-center gap-4 flex-wrap">
        <!-- Total Ventas Cerradas -->
        <div class="flex items-center gap-3 p-3 px-4 rounded-xl bg-n-alpha-1 border border-n-container min-w-[170px]">
          <div class="flex items-center justify-center size-10 rounded-lg bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <span class="i-lucide-badge-check text-xl flex-shrink-0" />
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11 font-medium">
              {{ t('OVERVIEW_REPORTS.WON_SALES.TOTAL_WON') }}
            </span>
            <span class="text-2xl font-bold text-n-slate-12">
              {{ totalWonCount }}
            </span>
          </div>
        </div>

        <!-- Tiempo Promedio de Cierre -->
        <div
          v-if="avgClosingTime > 0"
          class="flex items-center gap-3 p-3 px-4 rounded-xl bg-n-alpha-1 border border-n-container min-w-[170px]"
        >
          <div class="flex items-center justify-center size-10 rounded-lg bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
            <span class="i-lucide-timer text-xl flex-shrink-0" />
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11 font-medium">
              {{ t('OVERVIEW_REPORTS.WON_SALES.AVG_TIME') }}
            </span>
            <span class="text-2xl font-bold text-n-slate-12">
              {{ formatTime(avgClosingTime) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Contenedor del Gráfico de Barras -->
      <div class="w-full h-64 relative">
        <div v-if="timeseriesData.length && hasData" class="w-full h-full">
          <BarChart :collection="chartCollection" />
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center h-full text-n-slate-10 text-sm gap-2"
        >
          <span class="i-lucide-bar-chart-2 text-3xl text-n-slate-8" />
          <span>{{ t('OVERVIEW_REPORTS.WON_SALES.NO_DATA') }}</span>
        </div>
      </div>
    </div>
  </MetricCard>
</template>
